/**
 * HotspotLocationCard — W4.7 / Byzantine Review Fix
 *
 * Expanded standort- und Metadaten: Wallet, City, Maker, Elevation, Gain,
 * Onboarded date, Reward scale, Transmit scale, Address with copy.
 */
import { formatDistanceToNow } from 'date-fns'
import { useTranslation } from 'react-i18next'
import { Link } from '@tanstack/react-router'
import type { HotspotDetail } from './useHotspotDetail'
import { fmt } from '../../lib/fmt'
import { activeBcp47, activeDateFnsLocale } from '../../lib/locale'
import { explorerUrlFor } from './explorerUrl'

// --- Hilfsfunktionen ---

function shortPubkey(key: string | null | undefined): string {
  if (!key) return '—'
  if (key.length <= 14) return key
  return `${key.slice(0, 6)}…${key.slice(-4)}`
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleDateString(activeBcp47(), {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    })
  } catch {
    return iso
  }
}

/** Relative Angabe wie in v1 ("vor 2 Jahren") — ergaenzt das absolute Datum. */
function relativeDate(iso: string | null | undefined): string | null {
  if (!iso) return null
  try {
    return formatDistanceToNow(new Date(iso), { addSuffix: true, locale: activeDateFnsLocale() })
  } catch {
    return null
  }
}

// --- DataRow ---

type DataRowProps = {
  label: string
  value: React.ReactNode
  icon?: string
}

function DataRow({ label, value, icon }: DataRowProps) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: 12,
        padding: '8px 0',
        borderBottom: '1px solid var(--border)',
        // P2-Fix (Abnahme hotspot-detail, 390px): flexWrap statt starrem
        // Nebeneinander — Label und Wert (z.B. der Blockchain-Adress-Chip)
        // dürfen bei schmalen Viewports je eine eigene Zeile bekommen, statt
        // sich in eine Spalte zu quetschen und dadurch Zeichen-für-Zeichen
        // umzubrechen (wordBreak: break-all).
        flexWrap: 'wrap',
      }}
    >
      <span className="muted" style={{ fontSize: 12, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
        {icon && <span style={{ fontSize: 14 }}>{icon}</span>}
        {label}
      </span>
      <span
        style={{
          fontSize: 13,
          fontWeight: 500,
          textAlign: 'right',
          whiteSpace: 'nowrap',
          marginLeft: 'auto',
        }}
      >
        {value}
      </span>
    </div>
  )
}

// --- Props ---

type Props = {
  hotspot: HotspotDetail
}

export function HotspotLocationCard({ hotspot }: Props) {
  const { t } = useTranslation('hotspots')
  // S10-11: URL wird im FE aus address + device_type gebaut (iot-/mobile-Pfad);
  // bei unbekanntem/fehlendem device_type bisheriges Verhalten (BE-explorer_url,
  // sonst genereller Hotspot-Pfad). Siehe explorerUrl.ts.
  const explorer = explorerUrlFor(hotspot.address, hotspot.device_type, hotspot.explorer_url)
  // Stream H (R4): gain kommt als Zehntel-dBi vom BE. gain_dbi ist der bereits
  // geteilte Anzeigewert — die vorherige Anzeige von `gain` war Faktor 10 zu hoch.
  const gainDbi = hotspot.gain_dbi ?? (hotspot.gain != null ? hotspot.gain / 10 : null)
  const elevation = hotspot.elevation_m ?? hotspot.elevation
  const addedRelative = relativeDate(hotspot.added_at)
  return (
    <div className="card card-pad" aria-label={t('location.cardLabel')}>
      <h3 className="section-heading" style={{ marginBottom: 12 }}>
        {t('location.heading')}
      </h3>

      {/* Address */}
      {hotspot.street && (
        <DataRow
          icon="📍"
          label={t('location.labelAddress')}
          value={hotspot.street}
        />
      )}

      {/* Stream H (R4): Der zuvor doppelte Explorer-Link (Standalone + DataRow
          "Blockchain") ist auf die DataRow unten reduziert. */}

      {/* City with hotspot count — verlinkt auf die oeffentliche Staedteseite
          (Staedteseiten-Relaunch 09.2026), sofern city_id vorliegt. */}
      <DataRow
        icon="🏙"
        label={t('location.labelCity')}
        value={
          hotspot.city_id != null && hotspot.city_name ? (
            <Link to="/cities/$id" params={{ id: String(hotspot.city_id) }}>
              {[hotspot.city_name, hotspot.country].filter(Boolean).join(', ')}
            </Link>
          ) : (
            [hotspot.city_name, hotspot.country].filter(Boolean).join(', ') || '—'
          )
        }
      />

      {/* Maker */}
      <DataRow
        icon="🏷"
        label={t('location.labelMaker')}
        value={
          hotspot.maker_name
            ? `${hotspot.maker_name}${hotspot.device_type ? ` · ${hotspot.device_type}` : ''}`
            : '—'
        }
      />

      {/* Elevation */}
      <DataRow
        icon="⬆"
        label={t('location.labelElevation')}
        value={elevation != null ? t('location.valueMeters', { value: elevation }) : '—'}
      />

      {/* Antenna gain — gain_dbi, nicht der Zehntel-dBi-Rohwert */}
      <DataRow
        icon="📡"
        label={t('location.labelGain')}
        value={
          gainDbi != null ? (
            <span data-testid="location-gain">{t('location.valueDbi', { value: fmt(gainDbi, 1) })}</span>
          ) : (
            '—'
          )
        }
      />

      {/* Added (= created_at, v1-Semantik) — absolut + relativ */}
      <DataRow
        icon="💡"
        label={t('location.labelOnboarded')}
        value={
          <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
            <span>{formatDate(hotspot.added_at)}</span>
            {addedRelative && (
              <span className="muted" style={{ fontSize: 11 }} data-testid="location-added-relative">
                {addedRelative}
              </span>
            )}
          </span>
        }
      />

      {/* Reward scale */}
      <DataRow
        icon="🎯"
        label={t('location.labelRewardScale')}
        value={hotspot.reward_scale != null ? fmt(hotspot.reward_scale, 2) : '—'}
      />

      {/* Transmit scale */}
      <DataRow
        icon="📤"
        label={t('location.labelTransmitScale')}
        value={hotspot.reward_scale != null ? fmt(hotspot.reward_scale, 2) : '—'}
      />

      {/* Wallet with hotspot count */}
      {hotspot.wallet_address && (
        <DataRow
          icon="👛"
          label={t('location.labelWallet')}
          value={
            <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2, whiteSpace: 'normal' }}>
              <span className="addr-chip mono" title={hotspot.wallet_address} style={{ whiteSpace: 'nowrap' }}>
                {shortPubkey(hotspot.wallet_address)}
              </span>
              {hotspot.wallet_hotspot_count != null && (
                <span className="muted" style={{ fontSize: 11 }}>
                  {t('location.walletsInWallet', { count: hotspot.wallet_hotspot_count })}
                </span>
              )}
            </span>
          }
        />
      )}

      {/* Blockchain explorer link — einzige Explorer-Verlinkung der Karte */}
      {explorer && (
        <DataRow
          icon="🔗"
          label={
            hotspot.entity_type === 'validator'
              ? t('location.labelBlockchainValidator')
              : t('location.labelBlockchain')
          }
          value={
            <a
              href={explorer}
              target="_blank"
              rel="noopener noreferrer"
              className="addr-chip mono"
              title={hotspot.address ?? undefined}
              aria-label={t('location.explorerLabel')}
              style={{ color: 'var(--primary)' }}
            >
              {shortPubkey(hotspot.address)} ↗
            </a>
          }
        />
      )}
    </div>
  )
}
